import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, BookOpen, Check, Headphones, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/data/landing";
import { useExitIntent, markLeadDone } from "@/hooks/useExitIntent";

const Form = ({ onDone }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { name, email });
      toast.success(res.data.message || "Success! Check your inbox.");
      markLeadDone();
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="exit-intent-form">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name (optional)" data-testid="exit-intent-name" className="w-full rounded-xl border border-afwn-navy/10 bg-afwn-bg px-4 py-3 text-sm outline-none focus:border-afwn-green focus:ring-2 focus:ring-afwn-green/20" />
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid="exit-intent-email" className="w-full rounded-xl border border-afwn-navy/10 bg-afwn-bg px-4 py-3 text-sm outline-none focus:border-afwn-green focus:ring-2 focus:ring-afwn-green/20" />
      <button type="submit" disabled={loading} data-testid="exit-intent-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full font-medium px-6 py-3.5 bg-afwn-green text-white hover:bg-afwn-greenDark transition-all duration-300 disabled:opacity-70">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <>Send My Free Copy <ArrowRight size={16} /></>}
      </button>
      <p className="text-[11px] text-center text-afwn-slate">We respect your privacy. Unsubscribe anytime.</p>
    </form>
  );
};

export const ExitIntentOffer = () => {
  const [open, setOpen] = useExitIntent();
  const [done, setDone] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="exit-intent-modal" className="max-w-2xl p-0 overflow-hidden rounded-3xl border-0 bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-5">
          <div className="hidden sm:flex sm:col-span-2 bg-afwn-amber afwn-grain items-center justify-center p-8">
            <img src="/ebook/cover.jpg" alt="Anxiety Relief in 7 Minutes e-book cover" className="w-36 rounded-md shadow-2xl shadow-afwn-navy/30 ring-1 ring-white/30 -rotate-3" />
          </div>
          <div className="sm:col-span-3 p-8">
            {done ? (
              <div className="text-center py-6" data-testid="exit-intent-success">
                <div className="mx-auto w-14 h-14 rounded-full bg-afwn-green/10 flex items-center justify-center"><Check className="text-afwn-green" /></div>
                <DialogTitle className="mt-4 font-heading font-light text-3xl text-afwn-navy">You’re all set!</DialogTitle>
                <DialogDescription className="mt-2 text-afwn-slate">Check your inbox — your e-book and audiobook are on their way.</DialogDescription>
              </div>
            ) : (
              <>
                <span className="inline-flex items-center gap-2 px-3 py-1 text-xs tracking-wider uppercase rounded-full font-semibold bg-afwn-amber/15 text-afwn-amber"><BookOpen size={13} /> Before you go</span>
                <DialogTitle className="mt-4 font-heading font-light text-3xl sm:text-4xl leading-[1.1] text-afwn-navy">Take “Anxiety Relief in 7 Minutes” with you — free.</DialogTitle>
                <DialogDescription className="mt-3 text-afwn-slate">Practical, calming tools you can use today. The e-book and audiobook land in your inbox in seconds.</DialogDescription>
                <div className="mt-4 flex items-center gap-5 text-afwn-slate text-xs">
                  <span className="flex items-center gap-1.5"><BookOpen size={14} /> E-book (PDF)</span>
                  <span className="flex items-center gap-1.5"><Headphones size={14} /> Audiobook</span>
                </div>
                <Form onDone={() => setDone(true)} />
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
